import { useState, useRef, useEffect } from "react"
import { X, Settings, Clock, Tag, ChevronRight, Plus } from "lucide-react"

// ── Typed duration input ──────────────────────────────────────────────────────
const DurationInput = ({ value, onChange, min, max }) => {
  const [raw, setRaw] = useState(String(value))
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => {
    setRaw(String(value))
    setError(null)
  }, [value, min, max])

  const commit = (str) => {
    const num = parseInt(str, 10)
    if (!str.trim() || isNaN(num)) {
      setError(`Enter a number between ${min}–${max}`)
      return
    }
    if (num < min) {
      setError(`Minimum is ${min} mins`)
      onChange(min)
      setRaw(String(min))
    } else if (num > max) {
      setError(`Maximum is ${max} mins`)
      onChange(max)
      setRaw(String(max))
    } else {
      setError(null)
      onChange(num)
      setRaw(String(num))
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={`flex items-center border bg-white overflow-hidden w-32 transition-colors
          ${error ? "border-red-300" : "border-zinc-200 focus-within:border-zinc-400"}`}
        style={{ borderRadius: 0 }}
      >
        <input
          ref={inputRef}
          type="number"
          min={min}
          max={max}
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value)
            setError(null)
          }}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              commit(raw)
              inputRef.current?.blur()
            }
          }}
          className="flex-1 min-w-0 px-3 py-2 text-sm text-zinc-800 focus:outline-none bg-transparent
                     [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
                     [&::-webkit-inner-spin-button]:appearance-none"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        />
        <div
          className="px-2.5 py-2 text-[10px] text-zinc-400 border-l border-zinc-200 bg-zinc-50 select-none shrink-0"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          min
        </div>
      </div>
      <div className="h-4">
        {error ? (
          <p
            className="text-[10px] text-red-400"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {error}
          </p>
        ) : (
          <p
            className="text-[12px] text-zinc-500"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            Between {min} and {max} minutes
          </p>
        )}
      </div>
    </div>
  )
}

// ── Main modal ────────────────────────────────────────────────────────────────
const InterviewSettingsModal = ({
  interviewMode = "human", // "human" | "ai"
  initialDuration,
  initialSkills = [],
  onSave,
  onClose,
}) => {
  const isAI = interviewMode === "ai"
  const durationMin = isAI ? 10 : 15
  const durationMax = isAI ? 30 : 120

  const defaultDuration = Math.min(
    durationMax,
    Math.max(durationMin, initialDuration || durationMin),
  )

  const [duration, setDuration] = useState(defaultDuration)
  const [skills, setSkills] = useState(initialSkills)
  const [skillInput, setSkillInput] = useState("")
  const [skillsError, setSkillsError] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const addSkill = () => {
    const trimmed = skillInput.trim()
    if (!trimmed) return
    if (!skills.includes(trimmed)) {
      setSkills((prev) => [...prev, trimmed])
      setSkillsError(null)
    }
    setSkillInput("")
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addSkill()
    }
    if (e.key === "Backspace" && skillInput === "" && skills.length > 0) {
      setSkills((prev) => prev.slice(0, -1))
    }
  }

  const removeSkill = (skill) =>
    setSkills((prev) => prev.filter((s) => s !== skill))

  const handleSave = () => {
    if (skills.length < 3) {
      setSkillsError("Add at least 3 skills to evaluate.")
      return
    }
    onSave({ duration, skills })
    onClose()
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <div
          className="bg-white border border-zinc-200 shadow-2xl flex flex-col overflow-hidden"
          style={{ borderRadius: 0, width: "min(480px, 96vw)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 bg-white shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 bg-zinc-900 flex items-center justify-center flex-shrink-0">
                <Settings size={13} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-zinc-900">
                  Interview Settings
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${isAI ? "bg-violet-400" : "bg-emerald-400"}`}
                  />
                  <p
                    className="text-[12px] text-zinc-400 uppercase tracking-widest font-medium"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    {isAI ? "AI Round" : "Human+Ai Round"}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700"
              style={{ borderRadius: 0 }}
            >
              <X size={15} />
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-col px-6 py-6 gap-6">
            {/* Duration */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock size={12} className="text-zinc-400" />
                <label className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide">
                  Interview Duration
                </label>
              </div>
              <DurationInput
                value={duration}
                onChange={setDuration}
                min={durationMin}
                max={durationMax}
              />
            </div>

            <div className="border-t border-zinc-100" />

            {/* Skills */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Tag size={12} className="text-zinc-400" />
                  <label className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide">
                    Skills to Evaluate
                  </label>
                </div>
                {skills.length > 0 && (
                  <span
                    className={`text-[10px] font-medium ${skills.length < 3 ? "text-amber-500" : "text-emerald-500"}`}
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    {skills.length < 3
                      ? `${3 - skills.length} more needed`
                      : `${skills.length} added ✓`}
                  </span>
                )}
              </div>

              {/* Tag input box */}
              <div
                className={`flex flex-wrap gap-1.5 px-3 py-2.5 border bg-zinc-50 min-h-[44px] cursor-text transition-colors
                  ${skillsError ? "border-red-300" : "border-zinc-200 focus-within:border-zinc-400"}`}
                style={{ borderRadius: 0 }}
                onClick={() => inputRef.current?.focus()}
              >
                {skills.map((skill) => (
                  <span
                    key={skill}
                    className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5"
                    style={{
                      borderRadius: 0,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                  >
                    {skill}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        removeSkill(skill)
                      }}
                      className="text-blue-300 hover:text-blue-600 transition-colors ml-0.5"
                    >
                      <X size={9} strokeWidth={2.5} />
                    </button>
                  </span>
                ))}
                <input
                  ref={inputRef}
                  type="text"
                  value={skillInput}
                  onChange={(e) => {
                    setSkillInput(e.target.value)
                    setSkillsError(null)
                  }}
                  onKeyDown={handleKeyDown}
                  onBlur={addSkill}
                  placeholder={
                    skills.length === 0 ? "Type a skill and press Enter…" : ""
                  }
                  className="flex-1 min-w-[140px] bg-transparent text-[15px] text-zinc-700 placeholder-zinc-500 focus:outline-none"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                />
              </div>

              {skillsError && (
                <p
                  className="text-[10px] text-red-400 mt-1.5"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  ⚠ {skillsError}
                </p>
              )}

              {/* Suggestions */}
              {skills.length === 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {[
                    "Communication",
                    "Problem Solving",
                    "Leadership",
                    "Python",
                    "System Design",
                    "SQL",
                  ].map((s) => (
                    <button
                      key={s}
                      onClick={() =>
                        setSkills((prev) =>
                          prev.includes(s) ? prev : [...prev, s],
                        )
                      }
                      className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400 border border-zinc-200 bg-white px-2 py-1 hover:border-zinc-400 hover:text-zinc-600 transition-all"
                      style={{ borderRadius: 0 }}
                    >
                      <Plus size={8} strokeWidth={2.5} />
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between shrink-0">
            <p
              className="text-[11px] text-zinc-400"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {skills.length > 0
                ? `${skills.length} skill${skills.length > 1 ? "s" : ""} added`
                : "No skills added yet"}
            </p>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-5 py-2.5 text-[12px] font-bold uppercase tracking-widest border bg-zinc-900 border-zinc-900 text-white hover:bg-zinc-700 transition-all"
              style={{ borderRadius: 0 }}
            >
              Save Settings
              <ChevronRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default InterviewSettingsModal
